import { Banner, BlockStack, Button, Checkbox, ChoiceList, Form, FormLayout, Modal, Select, Spinner, Text, TextField } from '@shopify/polaris';
import { useCallback, useEffect, useMemo, useState } from 'react';
import helpers from 'helpers';
import { useField, useForm } from '@shopify/react-form';
import __ from 'languages/index';
import {
  TypedUser_tax_information,
  useMutateCreateUserTaxInformation,
  useMutateGetUserTaxInformation,
  useUpdateUserTaxInformation,
} from 'queries/user_tax_information.query';
import { PageDownIcon } from '@shopify/polaris-icons';
import { useSettings } from 'context/SettingsContext';

type BusinessTaxLookupResponse = {
  orgType?: string;
  taxID?: string;
  name?: string | null;
  address?: string | null;
  taxDepartment?: string;
  status?: string;
  updatedAt?: string;
};

export default function TaxInformationModal({
  onClose,
  show,
  currentTaxInformationId,
  user_id,
}: {
  onClose: Function;
  show: boolean;
  currentTaxInformationId?: string;
  user_id: string;
}) {
  const { mutateAsync: createEntity, isPending: creating, isSuccess: creatingSuccess } = useMutateCreateUserTaxInformation();
  const { mutateAsync: updateEntity, isPending: updating, isSuccess: updateSuccess } = useUpdateUserTaxInformation();
  const { mutateAsync: getEntity, data: entityData, isSuccess } = useMutateGetUserTaxInformation();

  const { settings } = useSettings();

  const [entity, setEntity] = useState<TypedUser_tax_information>(null);
  // const taxTypeOptions = [
  //   { label: 'Công ty', value: 'COMPANY' },
  //   { label: 'Cá nhân', value: 'PERSONAL' },
  // ];

  useEffect(() => {
    if (entityData) setEntity(entityData);
  }, [entityData]);

  useEffect(() => {
    if (currentTaxInformationId) {
      getEntity(currentTaxInformationId);
    } else {
      setEntity(null);
    }
  }, [currentTaxInformationId, show]);

  const useFields = {
    tax_type: useField<string>({ value: entity?.tax_type ?? 'COMPANY', validates: [] }),
    name: useField<string>({ value: entity?.name ?? undefined, validates: [] }),
    tax_code: useField<string>({ value: entity?.tax_code ?? undefined, validates: [] }),
    tax_cccd: useField<string>({ value: entity?.tax_cccd ?? undefined, validates: [] }),
    tax_mamoiquanhengansach: useField<string>({ value: entity?.tax_mamoiquanhengansach ?? undefined, validates: [] }),
    invoice_email: useField<string>({ value: entity?.invoice_email || '', validates: [] }),
    invoice_phone: useField<string>({ value: entity?.invoice_phone ?? undefined, validates: [] }),
    invoice_address: useField<string>({ value: entity?.invoice_address ?? undefined, validates: [] }),
    // contact_name: useField<string>({ value: entity?.contact_name ?? '', validates: [] }),
    // contact_email: useField<string>({ value: entity?.contact_email ?? '', validates: [] }),
    // contact_phone: useField<string>({ value: entity?.contact_phone ?? '', validates: [] }),
    // payment_method: useField<string>({ value: entity?.payment_method ?? '', validates: [] }),
    // bank_account_name: useField<string>({ value: entity?.bank_account_name ?? '', validates: [] }),
    // bank_account_number: useField<string>({ value: entity?.bank_account_number ?? '', validates: [] }),
    // bank_name: useField<string>({ value: entity?.bank_name ?? '', validates: [] }),
    note: useField<string>({ value: entity?.note ?? undefined, validates: [] }),
    is_default: useField<boolean>({ value: Boolean(entity?.is_default ?? 0), validates: [] }),
  };

  const [taxLookupError, setTaxLookupError] = useState('');
  const [taxLookupMessage, setTaxLookupMessage] = useState('');
  const [taxLookupLoading, setTaxLookupLoading] = useState(false);
  const normalizedTaxCode = useMemo(() => useFields.tax_code.value?.trim(), [useFields.tax_code.value]);

  const fetchBusinessTaxInformation = useCallback(async () => {
    const controller = new AbortController();
    setTaxLookupError('');
    setTaxLookupMessage('');
    setTaxLookupLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/taxfill/tax-payer/${encodeURIComponent(normalizedTaxCode)}`, { signal: controller.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const result = (await response.json()) as BusinessTaxLookupResponse;
      if (!result?.taxID || !result?.name) {
        throw new Error('Không tìm thấy doanh nghiệp tương ứng với mã số thuế đã nhập.');
      }

      if (controller.signal.aborted) return;

      let companyAddress = result.address?.trim().replace('TP ', 'Thành phố ') || '';
      if (!companyAddress.includes('Việt Nam')) {
        companyAddress = companyAddress + ', Việt Nam';
      }

      if (!(useFields.name.value || '').trim()) {
        useFields.name.onChange(result.name?.trim() || useFields.name.value);
      }
      if (!(useFields.invoice_address.value || '').trim()) {
        useFields.invoice_address.onChange(companyAddress || useFields.invoice_address.value);
      }

      setTaxLookupMessage(`Đã tự động điền thông tin công ty từ mã số thuế. ${result.status || ''}`);
    } catch (error: any) {
      if (controller.signal.aborted) return;
      setTaxLookupError(error?.message || 'Không thể tra cứu thông tin mã số thuế. Vui lòng kiểm tra lại.');
    } finally {
      if (controller.signal.aborted) return;
      setTaxLookupLoading(false);
    }
  }, [useFields.invoice_address, useFields.name, normalizedTaxCode]);

  useEffect(() => {
    setTaxLookupError('');
    setTaxLookupMessage('');
    setTaxLookupLoading(false);
  }, [normalizedTaxCode, show]);

  useEffect(() => {
    if (isSuccess && entityData) {
      useFields.tax_type.onChange(entityData.tax_type);
    }
  }, [isSuccess, entityData]);

  useEffect(() => {
    helpers.resetForm(useFields);
  }, [show]);

  const { fields, submit, submitting, dirty, reset } = useForm({
    fields: useFields,
    async onSubmit(values) {
      try {
        if (!currentTaxInformationId) {
          await createEntity({
            user_id,
            tax_type: values.tax_type,
            name: values.name,
            tax_code: values.tax_code,
            tax_cccd: values.tax_cccd,
            tax_mamoiquanhengansach: values.tax_mamoiquanhengansach,
            invoice_email: values.invoice_email,
            invoice_phone: values.invoice_phone,
            invoice_address: values.invoice_address,
            // contact_name: values.contact_name,
            // contact_email: values.contact_email,
            // contact_phone: values.contact_phone,
            // payment_method: values.payment_method,
            // bank_account_name: values.bank_account_name,
            // bank_account_number: values.bank_account_number,
            // bank_name: values.bank_name,
            note: values.note,
            is_default: values.is_default ? 1 : 0,
          });
        } else {
          await updateEntity({
            id: String(currentTaxInformationId),
            user_id,
            tax_type: values.tax_type,
            name: values.name,
            tax_code: values.tax_code,
            tax_cccd: values.tax_cccd,
            tax_mamoiquanhengansach: values.tax_mamoiquanhengansach,
            invoice_email: values.invoice_email,
            invoice_phone: values.invoice_phone,
            invoice_address: values.invoice_address,
            // contact_name: values.contact_name,
            // contact_email: values.contact_email,
            // contact_phone: values.contact_phone,
            // payment_method: values.payment_method,
            // bank_account_name: values.bank_account_name,
            // bank_account_number: values.bank_account_number,
            // bank_name: values.bank_name,
            note: values.note,
            is_default: values.is_default ? 1 : 0,
          });
        }
      } catch (e: any) {
        if (e.params !== undefined) {
          if (e.params.field !== undefined) {
            useFields[e.params.field].setError(__('check_this_field_again'));
          }
        }
      }
      return { status: 'success' };
    },
  });

  useEffect(() => {
    if (updateSuccess || creatingSuccess) {
      reset();
      onClose();
    }
  }, [updateSuccess, creatingSuccess]);

  useEffect(() => {
    if (!currentTaxInformationId) {
      reset();
      setEntity(null);
    }
  }, [currentTaxInformationId]);

  const modalMarkup = (
    <Modal
      open={show}
      onClose={() => onClose()}
      title={currentTaxInformationId ? 'Cập nhật thông tin thuế' : 'Thêm thông tin thuế'}
      primaryAction={{
        content: currentTaxInformationId ? 'Cập nhật' : 'Tạo',
        disabled: !dirty,
        loading: submitting,
        onAction: submit,
      }}
      secondaryActions={[
        {
          content: 'Close',
          onAction: () => onClose(),
        },
      ]}
    >
      <Modal.Section>
        <Form onSubmit={submit}>
          <FormLayout>
            <Text variant="headingSm" as="h3">
              Thông tin thuế
            </Text>
            {/* 
            <ChoiceList
              title="Loại thuế suất"
              choices={[
                {
                  label: 'Hóa đơn cá nhân',
                  value: 'PERSONAL',
                },
                {
                  label: 'Hóa đơn doanh nghiệp',
                  value: 'COMPANY',
                },
              ]}
              selected={[fields.tax_type.value]}
              onChange={(value) => fields.tax_type.onChange(value.pop())}
            /> */}

            {fields.tax_type.value === 'PERSONAL' ? (
              <BlockStack gap="100">
                <TextField autoComplete="off" label="Số căn cước công dân" readOnly={submitting} {...fields.tax_cccd} />
                <TextField autoComplete="off" label="Tên trên Căn cước" requiredIndicator readOnly={submitting} {...fields.name} />
              </BlockStack>
            ) : (
              <BlockStack gap="100">
                <TextField
                  autoComplete="off"
                  label="Mã số thuế"
                  clearButton
                  onClearButtonClick={() => {
                    fields.tax_code.onChange('');
                  }}
                  disabled={submitting}
                  connectedRight={
                    <Button onClick={() => fetchBusinessTaxInformation()} icon={PageDownIcon} disabled={submitting}>
                      Lấy thông tin
                    </Button>
                  }
                  {...fields.tax_code}
                />

                {taxLookupLoading ? (
                  <Text as="p" variant="bodySm" tone="subdued">
                    <Spinner size="small" /> Đang tra cứu thông tin doanh nghiệp...
                  </Text>
                ) : null}

                {taxLookupError ? <Banner tone="critical">{taxLookupError}</Banner> : null}
                {taxLookupMessage ? <Banner tone="success">{taxLookupMessage}</Banner> : null}
                <TextField autoComplete="off" label="Mã quan hệ ngân sách" disabled={submitting} {...fields.tax_mamoiquanhengansach} />
                <TextField autoComplete="off" label="Tên người mua" disabled={submitting} {...useFields.name} />

                <TextField autoComplete="off" label="Tên công ty" requiredIndicator disabled={submitting} {...useFields.name} />
              </BlockStack>
            )}

            <TextField label="Địa chỉ nhận hóa đơn" autoComplete="off" {...fields.invoice_address} />
            <TextField label="Email nhận hóa đơn" requiredIndicator autoComplete="off" {...fields.invoice_email} />
            <TextField label="Điện thoại nhận hóa đơn" autoComplete="off" {...fields.invoice_phone} />
            {/* <Divider />
            <Text variant="headingSm" as="h3">
              Thông tin liên hệ
            </Text>
            <FormLayout.Group>
              <TextField label="Người liên hệ" autoComplete="off" {...fields.contact_name} />
              <TextField label="Email liên hệ" autoComplete="off" {...fields.contact_email} />
              <TextField label="Điện thoại liên hệ" autoComplete="off" {...fields.contact_phone} />
            </FormLayout.Group>
            <Divider />
            <Text variant="headingSm" as="h3">
              Thông tin thanh toán
            </Text>
            <FormLayout.Group>
              <Select
                label="Phương thức thanh toán"
                options={paymentMethodOptions}
                value={fields.payment_method.value}
                onChange={fields.payment_method.onChange}
              />
            </FormLayout.Group>
            {fields.payment_method.value === 'TRANSFER' && (
              <>
                <FormLayout.Group>
                  <TextField label="Tên đứng tài khoản ngân hàng" autoComplete="off" {...fields.bank_account_name} />
                </FormLayout.Group>
                <FormLayout.Group>
                  <TextField label="Số tài khoản ngân hàng" autoComplete="off" {...fields.bank_account_number} />
                  <TextField label="Tên ngân hàng" autoComplete="off" {...fields.bank_name} />
                </FormLayout.Group>
              </>
            )}
            <Divider /> */}

            <TextField label="Ghi chú" autoComplete="off" multiline={4} value={fields.note.value} onChange={fields.note.onChange} />
            <Checkbox label="Đặt làm mặc định" checked={fields.is_default.value} onChange={(value) => fields.is_default.onChange(value)} />
          </FormLayout>
        </Form>
      </Modal.Section>
    </Modal>
  );

  return <>{modalMarkup}</>;
}

